// src/pages/dashboard/modules/restaurants/RestaurantLocationSettings.js
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Switch,
  FormControlLabel,
  TextField,
  Button,
  CircularProgress,
  Paper,
  Alert,
  Snackbar,
  Autocomplete,
  Avatar,
  Grid,
  Collapse,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import { useNavigate } from 'react-router-dom';
import { makeRequest } from '../../../assets/functions_helper';
import { motion } from 'framer-motion';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LockIcon from '@mui/icons-material/Lock';
import SaveIcon from '@mui/icons-material/Save';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import QRCodeCustomizer from './QRCodeCustomizer';

// Paleta de colores Premium & Moderna
const COLORS = {
  PRIMARY: '#f73541',
  PRIMARY_LIGHT: '#ffeff0',
  PRIMARY_HOVER: '#e62e3a',
  BACKGROUND: '#ffffff',
  CARD_BG: '#ffffff',
  TEXT_PRIMARY: '#0f172a',
  TEXT_SECONDARY: '#64748b',
  BORDER: '#e2e8f0',
  BORDER_FOCUS: '#f7354166',
  SHADOW_SOFT: '0 4px 20px rgba(15, 23, 42, 0.04)',
  SHADOW_MEDIUM: '0 12px 32px rgba(15, 23, 42, 0.08)',
};

const SectionCard = ({ title, icon, subtitle, children }) => (
  <Paper
    elevation={0}
    component={motion.div}
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4 }}
    sx={{
      p: { xs: 3, md: 4 },
      mb: 4,
      borderRadius: '24px',
      border: `1px solid ${COLORS.BORDER}`,
      backgroundColor: COLORS.CARD_BG,
      boxShadow: COLORS.SHADOW_SOFT,
      transition: 'all 0.3s ease-in-out',
      '&:hover': {
        boxShadow: COLORS.SHADOW_MEDIUM,
        transform: 'translateY(-2px)',
        borderColor: COLORS.BORDER_FOCUS,
      },
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 3 }}>
      <Box
        sx={{
          p: 1.5,
          borderRadius: '16px',
          background: COLORS.PRIMARY_LIGHT,
          color: COLORS.PRIMARY,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {React.cloneElement(icon, { fontSize: 'medium' })}
      </Box>
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 800, color: COLORS.TEXT_PRIMARY, fontFamily: 'Como, sans-serif' }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="body2" sx={{ color: COLORS.TEXT_SECONDARY, mt: 0.5, fontFamily: 'Como, sans-serif' }}>
            {subtitle}
          </Typography>
        )}
      </Box>
    </Box>
    {children}
  </Paper>
);

const containerStyle = {
  width: '100%',
  height: '400px',
  borderRadius: '12px',
};

const defaultCenter = {
  lat: 4.7110,
  lng: -74.0721,
};

const daysOfWeek = [
  { key: "monday", label: "Lunes" },
  { key: "tuesday", label: "Martes" },
  { key: "wednesday", label: "Miércoles" },
  { key: "thursday", label: "Jueves" },
  { key: "friday", label: "Viernes" },
  { key: "saturday", label: "Sábado" },
  { key: "sunday", label: "Domingo" },
];
const defaultHours = daysOfWeek.reduce((acc, day) => {
  acc[day.key] = { closed: true, open: "08:00", close: "20:00" };
  return acc;
}, {});

function RestaurantLocationSettings({ restaurantId, onNameUpdate }) {
  // Estado para nombre del restaurante
  const [restaurantName, setRestaurantName] = useState('');
  const [loadingName, setLoadingName] = useState(true);
  const [savingName, setSavingName] = useState(false);

  // Estado para ubicación y geolocalización
  const [location, setLocation] = useState({
    latitude: 0,
    longitude: 0,
    require_geolocation: false,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [markerPosition, setMarkerPosition] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Estado para código de acceso
  const [codeSettings, setCodeSettings] = useState({
    required_code: false,
    code: '',
  });
  const [loadingCode, setLoadingCode] = useState(true);
  const [updatingCode, setUpdatingCode] = useState(false);

  const [whatsappPhone, setWhatsappPhone] = useState('');
  const [addressInput, setAddressInput] = useState('');
  const [whatsappCountry, setWhatsappCountry] = useState(null);
  const [countries, setCountries] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [savingWhatsapp, setSavingWhatsapp] = useState(false);

  const navigate = useNavigate();

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
  });

  const [googleAnalyticsId, setGoogleAnalyticsId] = useState('');
  const [savingAnalytics, setSavingAnalytics] = useState(false);

  const [operatingHours, setOperatingHours] = useState(defaultHours);
  const [timezone, setTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [slotDuration, setSlotDuration] = useState(4);
  const [savingHours, setSavingHours] = useState(false);

  // Obtener nombre del restaurante
  const fetchRestaurantName = useCallback(() => {
    setLoadingName(true);
    makeRequest(
      'GET',
      `restaurants/restaurant/info?restaurant_id=${restaurantId}`,
      null,
      'application/json',
      localStorage.getItem('token'),
      localStorage.getItem('tokenType'),
      null,
      null,
      () => navigate('/login')
    )
      .then(data => {
        setRestaurantName(data.name || '');
        setLoadingName(false);
      })
      .catch(error => {
        console.error('Error fetching restaurant name:', error);
        setSnackbar({ open: true, message: 'Error al cargar el nombre del restaurante', severity: 'error' });
        setLoadingName(false);
      });
  }, [restaurantId, navigate]);

  // Obtener configuración de ubicación
  const fetchLocation = useCallback(() => {
    setLoading(true);
    makeRequest(
      'GET',
      `restaurant_admin/restaurant_location/${restaurantId}`,
      null,
      'application/json',
      localStorage.getItem('token'),
      localStorage.getItem('tokenType'),
      null,
      null,
      () => navigate('/login')
    )
      .then(data => {
        setLocation(data);
        if (data.require_geolocation && data.latitude && data.longitude) {
          const pos = { lat: data.latitude, lng: data.longitude };
          setMarkerPosition(pos);
          setMapCenter(pos);
        }
        setLoading(false);
      })
      .catch(error => {
        console.error('Error fetching location:', error);
        setSnackbar({ open: true, message: 'Error al cargar la ubicación', severity: 'error' });
        setLoading(false);
      });
  }, [restaurantId, navigate]);

  const fetchCodeSettings = useCallback(() => {
    setLoadingCode(true);
    makeRequest(
      'GET',
      `restaurant_admin/restaurant_code/${restaurantId}`,
      null,
      'application/json',
      localStorage.getItem('token'),
      localStorage.getItem('tokenType'),
      null,
      null,
      () => navigate('/login')
    )
      .then(data => {
        setCodeSettings({
          required_code: data.required_code,
          code: data.code || '',
        });
        setLoadingCode(false);
      })
      .catch(error => {
        console.error('Error fetching code settings:', error);
        setSnackbar({ open: true, message: 'Error al cargar configuración de código', severity: 'error' });
        setLoadingCode(false);
      });
  }, [restaurantId, navigate]);

  // Obtener WhatsApp
  const fetchWhatsappPhone = useCallback(() => {
    makeRequest(
      'GET',
      `restaurant_admin/whatsapp_phone/${restaurantId}`,
      null,
      'application/json',
      localStorage.getItem('token'),
      localStorage.getItem('tokenType')
    )
      .then((data) => {
        if (data) {
          if (data.whatsapp_phone) {
            setWhatsappPhone(data.whatsapp_phone);
          }
          if (data.address) {
            setAddressInput(data.address);
          }
        }
      })
      .catch(() => {});
  }, [restaurantId]);

  // Obtener Google Analytics
  const fetchAnalytics = useCallback(() => {
    makeRequest(
      'GET',
      `restaurant_admin/google_analytics/${restaurantId}`,
      null,
      'application/json',
      localStorage.getItem('token'),
      localStorage.getItem('tokenType')
    )
      .then((data) => {
        if (data && data.google_analytics_measurement_id) {
          setGoogleAnalyticsId(data.google_analytics_measurement_id);
        }
      })
      .catch(() => {});
  }, [restaurantId]);

  // Obtener Operating Hours
  const fetchOperatingHours = useCallback(() => {
    makeRequest(
      'GET',
      `reservations/operating-hours?restaurant_public_id=${localStorage.getItem('restaurant_public_id') || restaurantId}`,
      null,
      'application/json',
      localStorage.getItem('token'),
      localStorage.getItem('tokenType')
    )
      .then((data) => {
        if (data) {
          if (data.timezone) setTimezone(data.timezone);
          if (data.slot_duration_hours) setSlotDuration(data.slot_duration_hours);
          if (data.operating_hours) {
            const mergedHours = { ...defaultHours };
            for (const key of Object.keys(data.operating_hours)) {
              if (mergedHours[key]) mergedHours[key] = data.operating_hours[key];
            }
            setOperatingHours(mergedHours);
          }
        }
      })
      .catch(() => {});
  }, [restaurantId]);

  // Cargar países para el selector de código WhatsApp (mismo endpoint que registro)
  useEffect(() => {
    setLoadingCountries(true);
    makeRequest(
      'POST',
      'users/countries',
      {},
      'application/json',
      localStorage.getItem('token'),
      localStorage.getItem('tokenType')
    )
      .then((data) => setCountries(Array.isArray(data) ? data : []))
      .catch(() => {})
      .finally(() => setLoadingCountries(false));
  }, []);

  useEffect(() => {
    fetchRestaurantName();
    fetchLocation();
    fetchCodeSettings();
    fetchWhatsappPhone();
    fetchAnalytics();
    fetchOperatingHours();
  }, [fetchRestaurantName, fetchLocation, fetchCodeSettings, fetchWhatsappPhone, fetchAnalytics, fetchOperatingHours]);

  // Handlers para nombre
  const handleUpdateName = () => {
    const trimmedName = restaurantName.trim();
    if (!trimmedName) {
      setSnackbar({ open: true, message: 'El nombre no puede estar vacío', severity: 'warning' });
      return;
    }
    setSavingName(true);
    makeRequest(
      'PUT',
      `restaurant_admin/update_restaurant_name/${restaurantId}`,
      { name: trimmedName },
      'application/json',
      localStorage.getItem('token'),
      localStorage.getItem('tokenType'),
      null,
      null,
      () => navigate('/login')
    )
      .then(() => {
        setSnackbar({ open: true, message: 'Nombre actualizado correctamente', severity: 'success' });
        // Notificar al componente padre que el nombre ha cambiado
        if (onNameUpdate && trimmedName) {
          onNameUpdate(trimmedName);
        }
      })
      .catch(error => {
        console.error('Error updating name:', error);
        setSnackbar({ open: true, message: 'Error al actualizar el nombre', severity: 'error' });
      })
      .finally(() => setSavingName(false));
  };

  // Handlers para ubicación
  const handleToggleRequire = (event) => {
    const checked = event.target.checked;
    setLocation(prev => ({ ...prev, require_geolocation: checked }));
    if (!checked) {
      setMarkerPosition(null);
    } else {
      if (!markerPosition) {
        setMarkerPosition(mapCenter);
        setLocation(prev => ({ ...prev, latitude: mapCenter.lat, longitude: mapCenter.lng }));
      }
    }
  };

  const handleMapClick = (e) => {
    if (!location.require_geolocation) return;
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    setMarkerPosition({ lat, lng });
    setLocation(prev => ({ ...prev, latitude: lat, longitude: lng }));
  };

  const handleMarkerDragEnd = (e) => {
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    setMarkerPosition({ lat, lng });
    setLocation(prev => ({ ...prev, latitude: lat, longitude: lng }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let num = parseFloat(value);
    if (isNaN(num)) num = 0;
    setLocation(prev => ({ ...prev, [name]: num }));
    if (name === 'latitude' || name === 'longitude') {
      const newLat = name === 'latitude' ? num : location.latitude;
      const newLng = name === 'longitude' ? num : location.longitude;
      if (location.require_geolocation && newLat && newLng) {
        setMarkerPosition({ lat: newLat, lng: newLng });
        setMapCenter({ lat: newLat, lng: newLng });
      }
    }
  };

  // Handler para código
  const handleToggleCodeRequired = (event) => {
    const newValue = event.target.checked;
    setUpdatingCode(true);

    makeRequest(
      'PUT',
      `restaurant_admin/code/${restaurantId}?require_code=${newValue}`,
      {},
      'application/json',
      localStorage.getItem('token'),
      localStorage.getItem('tokenType'),
      null,
      null,
      () => navigate('/login')
    )
      .then(data => {
        setCodeSettings({
          required_code: newValue,
          code: newValue ? data.restaurant_new_code : '',
        });
        setSnackbar({
          open: true,
          message: newValue ? 'Código activado correctamente' : 'Código desactivado',
          severity: 'success',
        });
      })
      .catch(error => {
        console.error('Error updating code requirement:', error);
        setSnackbar({ open: true, message: 'Error al actualizar el requerimiento de código', severity: 'error' });
        setCodeSettings(prev => ({ ...prev, required_code: !newValue }));
      })
      .finally(() => setUpdatingCode(false));
  };

  // Handler para WhatsApp
  const handleUpdateWhatsapp = () => {
    const cleanNumber = whatsappPhone.replace(/[^0-9]/g, '').replace(/^0+/, '');
    if (!cleanNumber) {
      setSnackbar({ open: true, message: 'El número no puede estar vacío', severity: 'warning' });
      return;
    }
    setSavingWhatsapp(true);
    makeRequest(
      'PUT',
      `restaurant_admin/whatsapp_phone/${restaurantId}`,
      { whatsapp_phone: cleanNumber, address: addressInput.trim() || null },
      'application/json',
      localStorage.getItem('token'),
      localStorage.getItem('tokenType'),
      null,
      null,
      () => navigate('/login')
    )
      .then(() => {
        setSnackbar({ open: true, message: `Teléfono actualizado: ${cleanNumber}`, severity: 'success' });
      })
      .catch(() => {
        setSnackbar({ open: true, message: 'Error al actualizar WhatsApp', severity: 'error' });
      })
      .finally(() => setSavingWhatsapp(false));
  };

  // Guardar ubicación
  const handleSave = () => {
    if (location.require_geolocation && (!location.latitude || !location.longitude)) {
      setSnackbar({ open: true, message: 'Debe seleccionar una ubicación en el mapa', severity: 'warning' });
      return;
    }
    setSaving(true);
    const payload = {
      longitude: location.longitude,
      latitude: location.latitude,
      require_geolocation: location.require_geolocation,
    };
    makeRequest(
      'PUT',
      `restaurant_admin/restaurant_location/${restaurantId}`,
      payload,
      'application/json',
      localStorage.getItem('token'),
      localStorage.getItem('tokenType'),
      null,
      null,
      () => navigate('/login')
    )
      .then(() => {
        setSnackbar({ open: true, message: 'Ubicación guardada correctamente', severity: 'success' });
        fetchLocation();
      })
      .catch(error => {
        console.error('Error saving location:', error);
        setSnackbar({ open: true, message: 'Error al guardar la ubicación', severity: 'error' });
      })
      .finally(() => setSaving(false));
  };

  const handleUpdateAnalytics = () => {
    setSavingAnalytics(true);
    makeRequest(
      'PUT',
      `restaurant_admin/google_analytics/${restaurantId}`,
      { google_analytics_measurement_id: googleAnalyticsId.trim() || null },
      'application/json',
      localStorage.getItem('token'),
      localStorage.getItem('tokenType'),
      null, null, () => navigate('/login')
    )
      .then(() => setSnackbar({ open: true, message: 'Google Analytics actualizado', severity: 'success' }))
      .catch(() => setSnackbar({ open: true, message: 'Error al actualizar Analytics', severity: 'error' }))
      .finally(() => setSavingAnalytics(false));
  };

  const handleHourChange = (dayKey, field, value) => {
    setOperatingHours(prev => ({ ...prev, [dayKey]: { ...prev[dayKey], [field]: value } }));
  };

  const handleUpdateOperatingHours = () => {
    if (!timezone) {
      setSnackbar({ open: true, message: 'Debe seleccionar una zona horaria válida', severity: 'warning' });
      return;
    }
    for (const day of daysOfWeek) {
      const data = operatingHours[day.key];
      if (!data.closed) {
        if (!data.open || !data.close) {
          setSnackbar({ open: true, message: `Falta hora para el ${day.label}`, severity: 'warning' });
          return;
        }
        if (data.open >= data.close) {
          setSnackbar({ open: true, message: `Error en ${day.label}: Apertura debe ser menor al cierre`, severity: 'warning' });
          return;
        }
      }
    }
    setSavingHours(true);
    makeRequest(
      'PUT',
      `reservations/operating-hours?restaurant_public_id=${localStorage.getItem('restaurant_public_id') || restaurantId}`,
      { operating_hours: operatingHours, timezone },
      'application/json',
      localStorage.getItem('token'),
      localStorage.getItem('tokenType'),
      null, null, () => navigate('/login')
    )
      .then(() => setSnackbar({ open: true, message: 'Horarios actualizados con éxito', severity: 'success' }))
      .catch(() => setSnackbar({ open: true, message: 'Error al guardar horarios', severity: 'error' }))
      .finally(() => setSavingHours(false));
  };

  const handleUpdateSlotDuration = (e) => {
    const value = e.target.value;
    setSlotDuration(value);
    setSavingHours(true);
    makeRequest(
      "PATCH",
      `reservations/slot-duration?restaurant_public_id=${localStorage.getItem('restaurant_public_id') || restaurantId}`,
      { slot_duration_hours: parseFloat(value) },
      "application/json",
      localStorage.getItem("token"),
      localStorage.getItem("tokenType")
    )
      .then(() => setSnackbar({ open: true, message: "Duración de reserva actualizada con éxito", severity: "success" }))
      .catch(() => setSnackbar({ open: true, message: "Error al actualizar duración de reserva", severity: "error" }))
      .finally(() => setSavingHours(false));
  };

  if (loading || loadingCode || loadingName) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
          bgcolor: COLORS.BACKGROUND,
        }}
      >
        <CircularProgress sx={{ color: COLORS.PRIMARY }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: { xs: 2, md: 6 },
        bgcolor: COLORS.BACKGROUND,
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: '1000px', // Limite de ancho para resoluciones grandes
        }}
      >
        {/* Encabezado Principal */}
        <Box sx={{ mb: 6, textAlign: { xs: 'center', md: 'left' } }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 900,
              color: COLORS.TEXT_PRIMARY,
              mb: 1,
              fontFamily: 'Como, sans-serif',
              letterSpacing: '-1px',
            }}
          >
            Configuración del Restaurante
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: COLORS.TEXT_SECONDARY,
              fontWeight: 500,
              fontFamily: 'Como, sans-serif',
            }}
          >
            Gestiona la identidad, ubicación y canales de comunicación de tu negocio.
          </Typography>
        </Box>

        {/* Sección: Nombre */}
        <SectionCard
          title="Identidad"
          icon={<RestaurantIcon />}
          subtitle="Define cómo verán tus clientes el nombre de tu restaurante."
        >
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              fullWidth
              label="Nombre del Restaurante"
              value={restaurantName}
              onChange={(e) => setRestaurantName(e.target.value)}
              disabled={loadingName}
              size="medium"
              sx={{
                flex: 1,
                minWidth: 280,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '16px',
                  bgcolor: '#fff',
                },
              }}
            />
            <Button
              variant="contained"
              onClick={handleUpdateName}
              disabled={savingName || loadingName}
              sx={{
                backgroundColor: COLORS.PRIMARY,
                color: 'white',
                fontWeight: 800,
                px: 4,
                py: 1.5,
                borderRadius: '16px',
                textTransform: 'none',
                fontFamily: 'Como, sans-serif',
                boxShadow: '0 8px 20px rgba(247, 53, 65, 0.2)',
                '&:hover': {
                  backgroundColor: COLORS.PRIMARY_HOVER,
                  transform: 'translateY(-1px)',
                  boxShadow: '0 10px 24px rgba(247, 53, 65, 0.3)',
                },
              }}
              startIcon={savingName ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            >
              {savingName ? 'Guardando...' : 'Actualizar Nombre'}
            </Button>
          </Box>
        </SectionCard>

        {/* Sección: Geolocalización */}
        <SectionCard
          title="Geolocalización"
          icon={<LocationOnIcon />}
          subtitle="Controla si tus clientes deben estar físicamente cerca para usar el menú."
        >
          <Box
            sx={{
              p: 2,
              borderRadius: '20px',
              bgcolor: '#ffffff',
              border: `1px solid ${location.require_geolocation ? COLORS.PRIMARY : COLORS.BORDER}`,
              mb: 3,
              transition: 'all 0.3s ease',
            }}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={location.require_geolocation}
                  onChange={handleToggleRequire}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': { color: COLORS.PRIMARY },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: COLORS.PRIMARY },
                  }}
                />
              }
              label={
                <Typography sx={{ fontWeight: 700, fontFamily: 'Como, sans-serif', color: COLORS.TEXT_PRIMARY }}>
                  Requerir ubicación para acceder al menú
                </Typography>
              }
            />
          </Box>

          {location.require_geolocation && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 700, color: COLORS.TEXT_SECONDARY }}>
                Coordenadas y Mapa
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                <TextField
                  label="Latitud"
                  name="latitude"
                  type="number"
                  value={location.latitude}
                  onChange={handleInputChange}
                  size="small"
                  sx={{ flex: 1, minWidth: 140, '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                  inputProps={{ step: 'any' }}
                />
                <TextField
                  label="Longitud"
                  name="longitude"
                  type="number"
                  value={location.longitude}
                  onChange={handleInputChange}
                  size="small"
                  sx={{ flex: 1, minWidth: 140, '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                  inputProps={{ step: 'any' }}
                />
              </Box>
              <Box sx={{ borderRadius: '20px', overflow: 'hidden', border: `1px solid ${COLORS.BORDER}` }}>
                {isLoaded ? (
                  <GoogleMap
                    mapContainerStyle={containerStyle}
                    center={mapCenter}
                    zoom={15}
                    onClick={handleMapClick}
                    options={{
                      streetViewControl: false,
                      mapTypeControl: false,
                      styles: [
                        { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] },
                      ],
                    }}
                  >
                    {markerPosition && (
                      <Marker
                        position={markerPosition}
                        draggable={true}
                        onDragEnd={handleMarkerDragEnd}
                      />
                    )}
                  </GoogleMap>
                ) : (
                  <Box display="flex" justifyContent="center" p={4}>
                    <CircularProgress sx={{ color: COLORS.PRIMARY }} />
                  </Box>
                )}
              </Box>
              <Typography variant="caption" sx={{ mt: 1.5, display: 'block', color: COLORS.TEXT_SECONDARY, fontStyle: 'italic' }}>
                💡 Tip: Arrastra el marcador rojo para posicionar exactamente tu local en el mapa.
              </Typography>

              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  onClick={handleSave}
                  disabled={saving}
                  sx={{
                    backgroundColor: '#0f172a',
                    color: 'white',
                    fontWeight: 800,
                    px: 3,
                    py: 1.2,
                    borderRadius: '14px',
                    '&:hover': { backgroundColor: '#1e293b' },
                  }}
                  startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                >
                  {saving ? 'Guardando...' : 'Guardar Ubicación'}
                </Button>
              </Box>
            </motion.div>
          )}
        </SectionCard>

        {/* Sección: Código de Acceso */}
        <SectionCard
          title="Seguridad"
          icon={<LockIcon />}
          subtitle="Gestiona el código de acceso para los clientes en el local."
        >
          <Box
            sx={{
              p: 2,
              borderRadius: '20px',
              bgcolor: '#ffffff',
              border: `1px solid ${codeSettings.required_code ? COLORS.PRIMARY : COLORS.BORDER}`,
              mb: 3,
            }}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={codeSettings.required_code}
                  onChange={handleToggleCodeRequired}
                  disabled={updatingCode}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': { color: COLORS.PRIMARY },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: COLORS.PRIMARY },
                  }}
                />
              }
              label={
                <Typography sx={{ fontWeight: 700, fontFamily: 'Como, sans-serif' }}>
                  Requerir PIN de acceso al menú
                </Typography>
              }
            />
          </Box>

          {codeSettings.required_code && codeSettings.code && (
            <Box
              sx={{
                p: 3,
                backgroundColor: '#ffffff',
                borderRadius: '24px',
                textAlign: 'center',
                border: `2px dashed ${COLORS.PRIMARY}33`,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Typography variant="overline" sx={{ color: COLORS.TEXT_SECONDARY, fontWeight: 900 }}>
                CÓDIGO DE ACCESO ACTUAL
              </Typography>
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 900,
                  letterSpacing: 8,
                  color: COLORS.PRIMARY,
                  fontFamily: 'monospace',
                  textShadow: '0 4px 12px rgba(247, 53, 65, 0.1)',
                }}
              >
                {codeSettings.code}
              </Typography>
              <Typography variant="caption" sx={{ color: COLORS.TEXT_SECONDARY }}>
                Compártelo con tus clientes para que puedan pedir desde la mesa.
              </Typography>
            </Box>
          )}
        </SectionCard>

        {/* Sección: WhatsApp */}
        <SectionCard
          title="Canales de Venta"
          icon={<WhatsAppIcon />}
          subtitle="Recibe tus pedidos automáticos directamente en WhatsApp."
        >
          <Box sx={{ mb: 4 }}>
            <Typography variant="body2" sx={{ mb: 2, fontWeight: 600, color: COLORS.TEXT_SECONDARY }}>
              Configura tu número internacional para recibir pedidos del LinkTree.
            </Typography>

            {/* Selector de país optimizado */}
            <Autocomplete
              options={countries}
              loading={loadingCountries}
              loadingText="Cargando países..."
              getOptionLabel={(o) => `${o.name} (${o.code})`}
              value={whatsappCountry}
              onChange={(_, val) => setWhatsappCountry(val)}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              renderOption={(props, option) => (
                <Box component="li" {...props} key={option.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5 }}>
                  <Avatar src={option.flag} sx={{ width: 24, height: 24, border: '1px solid #e2e8f0' }} />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {option.name} <span style={{ color: COLORS.TEXT_SECONDARY, fontWeight: 400 }}>({option.code})</span>
                  </Typography>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="País / Código de área"
                  variant="outlined"
                  sx={{
                    mb: 3,
                    '& .MuiOutlinedInput-root': { borderRadius: '16px' },
                  }}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: whatsappCountry?.flag ? (
                      <Avatar src={whatsappCountry.flag} sx={{ width: 22, height: 22, ml: 1, mr: 0, border: '1px solid #e2e8f0' }} />
                    ) : null,
                  }}
                />
              )}
            />

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  height: '56px',
                  bgcolor: '#ffffff',
                  border: `1px solid ${COLORS.BORDER}`,
                  borderRadius: '16px',
                  px: 2,
                  minWidth: '85px',
                }}
              >
                <Typography variant="body1" sx={{ fontWeight: 900, color: COLORS.TEXT_PRIMARY }}>
                  {whatsappCountry?.code || '--'}
                </Typography>
              </Box>
              <TextField
                fullWidth
                label="Número de Teléfono"
                placeholder="300 123 4567"
                value={whatsappPhone}
                onChange={(e) => setWhatsappPhone(e.target.value.replace(/[^0-9]/g, ''))}
                disabled={savingWhatsapp}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '16px' } }}
              />
            </Box>
            
            <TextField
              fullWidth
              label="Dirección Física (Opcional, para recoger en local)"
              placeholder="Ej. Calle 123 #45-67"
              value={addressInput}
              onChange={(e) => setAddressInput(e.target.value)}
              disabled={savingWhatsapp}
              sx={{ mt: 3, '& .MuiOutlinedInput-root': { borderRadius: '16px' } }}
            />
          </Box>

          <Button
            fullWidth
            variant="contained"
            onClick={handleUpdateWhatsapp}
            disabled={savingWhatsapp || !whatsappPhone || !whatsappCountry}
            sx={{
              backgroundColor: '#25D366', // Color WhatsApp
              color: 'white',
              fontWeight: 800,
              py: 1.8,
              borderRadius: '16px',
              fontFamily: 'Como, sans-serif',
              boxShadow: '0 8px 16px rgba(37, 211, 102, 0.2)',
              '&:hover': {
                backgroundColor: '#1ebd5e',
                boxShadow: '0 10px 20px rgba(37, 211, 102, 0.3)',
              },
            }}
            startIcon={savingWhatsapp ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
          >
            {savingWhatsapp ? 'Guardando...' : 'Guardar Configuración de WhatsApp'}
          </Button>
        </SectionCard>

        <SectionCard
          title="Google Analytics"
          icon={<AnalyticsIcon />}
          subtitle="Monitorea el tráfico de tu menú público."
        >
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              fullWidth
              label="Measurement ID (Ej: G-XXXXXXX)"
              value={googleAnalyticsId}
              onChange={(e) => setGoogleAnalyticsId(e.target.value)}
              disabled={savingAnalytics}
              size="medium"
              sx={{ flex: 1, minWidth: 280, '& .MuiOutlinedInput-root': { borderRadius: '16px', bgcolor: '#fff' } }}
            />
            <Button
              variant="contained"
              onClick={handleUpdateAnalytics}
              disabled={savingAnalytics}
              sx={{
                backgroundColor: COLORS.PRIMARY, color: 'white', fontWeight: 800, px: 4, py: 1.5,
                borderRadius: '16px', textTransform: 'none', fontFamily: 'Como, sans-serif',
                boxShadow: '0 8px 20px rgba(247, 53, 65, 0.2)',
                '&:hover': { backgroundColor: COLORS.PRIMARY_HOVER, transform: 'translateY(-1px)', boxShadow: '0 10px 24px rgba(247, 53, 65, 0.3)' },
              }}
              startIcon={savingAnalytics ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            >
              {savingAnalytics ? 'Guardando...' : 'Guardar Analytics'}
            </Button>
          </Box>
        </SectionCard>

        {/* Sección: Horarios de Operación */}
        <SectionCard
          title="Horarios de Operación (Reservas)"
          icon={<AccessTimeIcon />}
          subtitle="Define en qué ventanas de tiempo estás disponible para recibir reservas."
        >
          <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
             <Autocomplete
                options={Intl.supportedValuesOf('timeZone')}
                value={timezone}
                onChange={(_, newVal) => setTimezone(newVal)}
                disabled={savingHours}
                disableClearable
                sx={{ flexGrow: 1, minWidth: 250 }}
                renderInput={(params) => (
                  <TextField {...params} label="Zona Horaria (Obligatorio)" sx={{ '& .MuiOutlinedInput-root': { borderRadius: '16px', bgcolor: '#fff' } }} />
                )}
             />

             <FormControl sx={{ flexGrow: 1, minWidth: 150 }}>
               <InputLabel>Duración de reserva</InputLabel>
               <Select
                 value={slotDuration}
                 label="Duración de reserva"
                 onChange={handleUpdateSlotDuration}
                 disabled={savingHours}
                 sx={{ borderRadius: '16px', bgcolor: '#fff' }}
               >
                  <MenuItem value={0.5}>30 minutos</MenuItem>
                  <MenuItem value={1}>1 hora</MenuItem>
                  <MenuItem value={1.5}>1.5 horas</MenuItem>
                  <MenuItem value={2}>2 horas</MenuItem>
                  <MenuItem value={2.5}>2.5 horas</MenuItem>
                  <MenuItem value={3}>3 horas</MenuItem>
                  <MenuItem value={4}>4 horas</MenuItem>
                  <MenuItem value={5}>5 horas</MenuItem>
                  <MenuItem value={6}>6 horas</MenuItem>
                  <MenuItem value={8}>8 horas</MenuItem>
                  <MenuItem value={12}>12 horas</MenuItem>
               </Select>
             </FormControl>
          </Box>
          <Grid container spacing={2}>
            {daysOfWeek.map((day) => {
              const data = operatingHours[day.key];
              return (
                <Grid item xs={12} key={day.key}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, borderRadius: '16px', border: `1px solid ${COLORS.BORDER}`, flexWrap: { xs: 'wrap', sm: 'nowrap' }, gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', minWidth: '140px' }}>
                      <Switch checked={!data.closed} onChange={(e) => handleHourChange(day.key, "closed", !e.target.checked)} disabled={savingHours} sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: COLORS.PRIMARY }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: COLORS.PRIMARY } }} />
                      <Typography sx={{ fontWeight: 700, fontFamily: 'Como, sans-serif', color: COLORS.TEXT_PRIMARY }}>{day.label}</Typography>
                    </Box>
                    <Collapse in={!data.closed} orientation="horizontal">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TextField type="time" value={data.open} onChange={(e) => handleHourChange(day.key, "open", e.target.value)} disabled={savingHours} size="small" inputProps={{ step: 300 }} sx={{ width: '130px', '& .MuiOutlinedInput-root': { borderRadius: '10px' } }} />
                        <Typography sx={{ color: COLORS.TEXT_SECONDARY }}>-</Typography>
                        <TextField type="time" value={data.close} onChange={(e) => handleHourChange(day.key, "close", e.target.value)} disabled={savingHours} size="small" inputProps={{ step: 300 }} sx={{ width: '130px', '& .MuiOutlinedInput-root': { borderRadius: '10px' } }} />
                      </Box>
                    </Collapse>
                    {data.closed && (
                       <Typography sx={{ color: COLORS.TEXT_SECONDARY, fontStyle: 'italic', pr: 2, fontFamily: 'Como, sans-serif' }}>Cerrado</Typography>
                    )}
                  </Box>
                </Grid>
              );
            })}
          </Grid>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
             <Button variant="contained" onClick={handleUpdateOperatingHours} disabled={savingHours} sx={{ backgroundColor: COLORS.PRIMARY, color: 'white', fontWeight: 800, px: 4, py: 1.5, borderRadius: '16px', textTransform: 'none', fontFamily: 'Como, sans-serif', boxShadow: '0 8px 20px rgba(247, 53, 65, 0.2)', '&:hover': { backgroundColor: COLORS.PRIMARY_HOVER, transform: 'translateY(-1px)', boxShadow: '0 10px 24px rgba(247, 53, 65, 0.3)' } }} startIcon={savingHours ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}>
               {savingHours ? 'Guardando...' : 'Guardar Horarios'}
             </Button>
          </Box>
        </SectionCard>

        {/* ── Sección QR Personalizado ── */}
        <Box sx={{ mt: 4, mb: 4 }}>
          <QRCodeCustomizer 
            restaurantPublicId={localStorage.getItem('restaurant_public_id') || restaurantId} 
            restaurantId={restaurantId}
          />
        </Box>

        {/* Footer info */}
        <Box sx={{ mt: 2, textAlign: 'center', opacity: 0.6 }}>
          <Typography variant="caption" sx={{ fontFamily: 'Como, sans-serif', fontWeight: 600 }}>
            Eatery v3.0 • Todos los cambios se guardan de forma segura
          </Typography>
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          variant="filled"
          sx={{
            borderRadius: '16px',
            fontFamily: 'Como, sans-serif',
            fontWeight: 700,
            boxShadow: '0 12px 32px rgba(0,0,0,0.15)',
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default RestaurantLocationSettings;